import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'

import { cn } from '@/lib/utils'

/**
 * v2 · 04 Field label — 15/500 sentence case in ink, sitting above
 * the input. Goes destructive when the field is invalid; there is no
 * accent focus colour any more — the field's own border carries
 * focus. State is driven by the wrapping Field via data attributes.
 */
const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      'text-[15px] font-medium leading-none text-ink',
      'transition-colors duration-[120ms] ease-standard',
      'group-data-[invalid]/field:text-destructive',
      'group-data-[disabled]/field:text-ink-faint',
      'peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
      className
    )}
    {...props}
  />
))
Label.displayName = LabelPrimitive.Root.displayName

/**
 * Field — label + control + hint/error, wired together.
 *
 * The 8px label→control gap and the label's state colours come from
 * here so every form in the app reads the same. `counter` renders in
 * the label row.
 */
interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: React.ReactNode
  htmlFor?: string
  required?: boolean
  hint?: React.ReactNode
  error?: React.ReactNode
  counter?: React.ReactNode
  disabled?: boolean
}

function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  counter,
  disabled,
  className,
  children,
  ...props
}: FieldProps) {
  return (
    <div
      className={cn('group/field flex flex-col gap-2', className)}
      data-invalid={error ? '' : undefined}
      data-disabled={disabled ? '' : undefined}
      {...props}
    >
      {(label || counter) && (
        <div className="flex items-baseline justify-between gap-3">
          {label && (
            <Label htmlFor={htmlFor}>
              {label}
              {required && ' *'}
            </Label>
          )}
          {counter && (
            <span className="text-[13px] leading-none text-ink-faint tabular-nums">
              {counter}
            </span>
          )}
        </div>
      )}
      {children}
      {error ? (
        <span className="text-[13px] leading-snug text-destructive">{error}</span>
      ) : hint ? (
        <span className="text-[13px] leading-snug text-ink-muted">{hint}</span>
      ) : null}
    </div>
  )
}

export { Label, Field }
